import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { useAllNewsQuery } from "@api";
import { SanityArticlesDisplay, SanityNews } from "@graphql-types";
import { useStore } from "@util/states";
import { Container, StandardLayoutContainer } from "@util/standard";
import { Dropdown } from "@global";
import { colors, mediaQuery, transitionAll } from "@util/constants";
import ArticlePreview from "./articlePreview";
import { sortArticles } from "@util/helper";

interface Props {
  data: SanityArticlesDisplay;
}

const CategoriesWrapper = styled(StandardLayoutContainer)`
  margin: 100px auto;
  justify-content: space-between;

  ${mediaQuery.largeDown} {
    flex-direction: column;
    justify-content: flex-start;
  }
`;

const CategoryTitle = styled.p<{ isFirst?: boolean; isSelected: boolean }>`
  padding: 0 40px;
  ${({ isFirst }) => isFirst && `padding-left: 0;`};
  border-right: 1px solid ${colors.lightGrey};
  height: max-content;
  opacity: 0.5;
  ${({ isSelected }) => isSelected && `opacity: 1;`};
  ${transitionAll}
  cursor: pointer;

  ${mediaQuery.largeDown} {
    width: 100%;
    padding: 0;
    margin-bottom: 15px;
  }
`;

function ArticlesDisplay({ data }: Props) {
  if (data == null) return null;

  const { currentRegion } = useStore();

  const newsData = useAllNewsQuery(currentRegion.iban);

  if (newsData == null) return null;

  const [sortedArticles, setSortedArticles] = useState<SanityNews[]>();
  const [currentCategory, setCurrentCategory] = useState("");
  const [sortValue, setSortValue] = useState("Sort");

  const { articles, categories } = newsData;
  const { featuredArticle, numberOfArticles } = data;

  const filteredArticles = articles.filter(article => {
    if (article?.slug?.current === featuredArticle?.slug?.current) {
      return false;
    }
    if (article == null || article.categories == null) return null;
    if (currentCategory === "") return article;

    return article.categories.some(category => category?.title === currentCategory);
  });

  useEffect(() => {
    const sortAllArticles = sortArticles(filteredArticles, sortValue);

    setSortedArticles(sortAllArticles);
  }, [sortValue, currentCategory]);
  return (
    <>
      <CategoriesWrapper>
        <Container width="80%" tabletWidth="100%" flexWrap="wrap">
          <CategoryTitle
            onClick={() => setCurrentCategory("")}
            isSelected={currentCategory === ""}
            isFirst
          >
            All
          </CategoryTitle>
          {categories.map((category, index) => {
            if (category?.title == null) return null;

            return (
              <CategoryTitle
                onClick={() => category.title && setCurrentCategory(category.title)}
                isSelected={category.title === currentCategory}
                key={category.title + index}
              >
                {category.title}
              </CategoryTitle>
            );
          })}
        </Container>
        <Container width="30%" tabletWidth="100%" maxWidth="350px" tabletMargin="40px 0 0 0">
          <Dropdown
            title={sortValue}
            onChange={setSortValue}
            data={["Latest", "Earliest", "Alphabetical (A-Z)", "Alphabetical (Z-A)"]}
          />
        </Container>
      </CategoriesWrapper>
      <div>
        {featuredArticle && currentCategory === "" && (
          <ArticlePreview data={featuredArticle} isFeaturedArticle />
        )}
        {sortedArticles && (
          <Container width="100%" flexWrap="wrap">
            {sortedArticles.map((article, index) => {
              if (article == null || index + 1 === numberOfArticles) return null;

              return (
                <Container width="50%" tabletWidth="100%" key={article._id}>
                  <ArticlePreview data={article} />
                </Container>
              );
            })}
          </Container>
        )}
      </div>
    </>
  );
}

export default ArticlesDisplay;
